import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChefHat, Settings, Users } from 'lucide-react';
import { useGameStateContext } from '../app/GameProviders';
import { SettingsMenu } from './SettingsMenu';
import { ASSETS } from '../assetManifest.js';
import { CONSTANTS } from '../data.js';

export function TitleMenu() {
  const { setGameState } = useGameStateContext();
  const [showSettings, setShowSettings] = useState(false);
  const [playButtonHovered, setPlayButtonHovered] = useState(false);
  const [settingsButtonHovered, setSettingsButtonHovered] = useState(false);

  const handleStartGame = () => {
    setGameState('loading');
  };

  // One-time asset validation in development
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      const bgCfg = (ASSETS as any)?.ui?.title_background;
      if (!bgCfg || !bgCfg.path || typeof bgCfg.path !== 'string') {
        throw new Error('ASSETS.ui.title_background.path is missing or invalid. Define a valid relative path in src/assetManifest.js');
      }
      if (bgCfg.path.startsWith('/')) {
        throw new Error("ASSETS.ui.title_background.path must be relative and must not start with '/'. Use e.g. 'assets/backgrounds/title-bg.webp'");
      }

      const playButtonCfg = (ASSETS as any)?.ui?.play_button;
      const playButtonHoverCfg = (ASSETS as any)?.ui?.play_button_hover;
      if (!playButtonCfg || !playButtonCfg.path || !playButtonHoverCfg || !playButtonHoverCfg.path) {
        throw new Error('Play button assets are missing or invalid in assetManifest.js');
      }

      const settingsButtonCfg = (ASSETS as any)?.ui?.settings_button;
      const settingsButtonHoverCfg = (ASSETS as any)?.ui?.settings_button_hover;
      if (!settingsButtonCfg || !settingsButtonCfg.path || !settingsButtonHoverCfg || !settingsButtonHoverCfg.path) {
        throw new Error('Settings button assets are missing or invalid in assetManifest.js');
      }
    }
  }, []);

  const backgroundImage: string = (ASSETS as any).ui.title_background.path;
  const playButtonCfg = (ASSETS as any).ui.play_button;
  const playButtonHoverCfg = (ASSETS as any).ui.play_button_hover;
  const settingsButtonCfg = (ASSETS as any).ui.settings_button;
  const settingsButtonHoverCfg = (ASSETS as any).ui.settings_button_hover;

  return (
    <div
      className="min-h-screen flex items-center justify-center text-white font-sans relative overflow-hidden"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#1e293b'
      }}
    >
      {/* Kitchen-themed gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-900/40 via-orange-900/30 to-red-900/40" />
      
      {/* Main menu content */}
      <div className="relative z-10 text-center">
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="mb-12"
        >
          {/* Kitchen-styled title container */}
          <div className="bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 rounded-2xl p-8 border border-amber-500/30 shadow-2xl mb-8">
            <div className="flex items-center justify-center gap-4 mb-4 pb-2">
              <ChefHat className="w-16 h-16 text-amber-100 drop-shadow-lg" />
              <h1 className="text-7xl font-bold bg-gradient-to-r from-amber-100 via-orange-100 to-red-100 bg-clip-text text-transparent pb-2">
                Delichemy
              </h1>
            </div>
            <p className="text-xl text-amber-100 max-w-2xl mx-auto leading-relaxed">
              Master the art of culinary alchemy! Combine ingredients to discover new recipes, 
              serve hungry customers, and build your cooking empire.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
          className="space-y-4"
        >
          {/* Start Cooking Button */}
          <motion.button
            onClick={handleStartGame}
            onMouseEnter={() => setPlayButtonHovered(true)}
            onMouseLeave={() => setPlayButtonHovered(false)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Start Cooking"
            className="block mx-auto transition-all duration-300 relative flex items-center justify-center bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 rounded-xl shadow-lg border border-emerald-400/30"
            style={{
              width: `${playButtonCfg.width || 200}px`,
              height: `${playButtonCfg.height || 60}px`,
            }}
          >
            <span className="text-white font-semibold text-xl drop-shadow">
              Start Cooking
            </span>
          </motion.button>

          {/* Settings Button */}
          <motion.button
            onClick={() => setShowSettings(true)}
            onMouseEnter={() => setSettingsButtonHovered(true)}
            onMouseLeave={() => setSettingsButtonHovered(false)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Settings"
            className="block mx-auto transition-all duration-300 relative flex items-center justify-center bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-500 hover:to-slate-600 rounded-xl shadow-lg border border-slate-400/30"
            style={{
              width: `${settingsButtonCfg.width || 160}px`,
              height: `${settingsButtonCfg.height || 50}px`,
            }}
          >
            <span className="text-white font-medium text-lg drop-shadow flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Settings
            </span>
          </motion.button>
        </motion.div>
      </div>

      {/* Settings Menu */}
      <SettingsMenu 
        show={showSettings} 
        onClose={() => setShowSettings(false)} 
      />
    </div>
  );
}
